import { defineStore } from "pinia";
import router, { resetTokenCheck } from "@/router";
import { ref } from "vue";
import type { User } from "@/types/auth";
import authApi from "@/api/auth/authApi";
import { TOKEN_KEY, hasToken } from "@/helpers/auth-token";

const STORAGE_KEY = "kyc_user";

const readStoredUser = (): User | null => {
  const raw =
    sessionStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
};

export const useAuthStore = defineStore("auth_store", () => {
  const user = ref<User | null>(readStoredUser());

  const saveSession = (
    newUser: User,
    permissions: string[],
    role: string,
    token: string,
    modules: unknown[] = [],
  ) => {
    user.value = newUser;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newUser));
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem("user_permissions", JSON.stringify(permissions));
    localStorage.setItem("user_role", role);
    localStorage.setItem("modules", JSON.stringify(modules));
  };

  const clearStorage = () => {
    user.value = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem("user_permissions");
    localStorage.removeItem("user_role");
    localStorage.removeItem("modules");
  };

  const logout = async () => {
    try {
      await authApi.logoutApi();
    } catch {
      // token may already be expired; clear locally regardless
    } finally {
      clearStorage();
      resetTokenCheck();
      router.push({ name: "auth.sign-in" });
    }
  };

  const refresh = async () => {
    try {
      const data = await authApi.refreshApi();
      saveSession(
        data.user,
        data.permissions ?? [],
        data.role ?? "guest",
        data.token,
        [],
      );
      return data.token as string;
    } catch {
      clearStorage();
      resetTokenCheck();
      router.push({ name: "auth.sign-in" });
      return null;
    }
  };

  const isAuthenticated = () => {
    return Boolean(user.value && hasToken());
  };

  return {
    user,
    saveSession,
    logout,
    refresh,
    isAuthenticated,
  };
});
